/**
 * Chat messages API — Clerk-authenticated SSE consumer for
 * `POST /v1/chat/messages` on towninsider-api.
 *
 * Replaces the v1 mock from earlier app batches. All intent detection
 * (weather / picks / tea / pick-ack) and reply generation now happens
 * server-side via Anthropic Sonnet 4.5 with tool use — this client is
 * a pure transport + state-management layer.
 *
 * Hook surface:
 *
 *   const { send, isStreaming, abort } = useSendMessage();
 *
 *   send({ text, dateLocal, clientNonce? }, {
 *     onTextDelta: (delta) => appendToPlaceholder(delta),
 *     onComplete:  (event) => finaliseStatus(),
 *     onError:     (event) => showPersonaErrorReply(),
 *   });
 *
 * Terminal paths:
 *   - `complete` — server emitted the SSE complete event after atomic
 *                  DB persistence succeeded. Both message IDs are
 *                  server-issued ULIDs; not adopted into client state
 *                  in this batch (Batch 1c, GET /history reconciliation,
 *                  decides the ID alignment strategy).
 *   - `error`    — pre-stream JSON envelope (auth, validation, rate
 *                  limit) OR mid-stream SSE error event
 *                  (persona_overloaded, persona_unreachable,
 *                  tool_loop_exceeded, empty_response,
 *                  persistence_failed). UI shows persona soft error.
 *   - `aborted`  — caller invoked abort(). UI stays silent; no
 *                  `onError` dispatch, so callers can fire `abort()`
 *                  from a useEffect cleanup without surfacing an error.
 *
 * Concurrency: one stream may be in flight at a time per hook instance.
 * Calling send() while a previous send is still streaming aborts the
 * previous one. Pages should additionally early-return their submit
 * handler when `isStreaming` is true — premium UX shows one bubble at
 * a time, never interleaved.
 *
 * Validation: every inbound SSE event's `data` payload is Zod-parsed
 * against the contract defined in `towninsider-api/src/routes/chat.ts`.
 * Malformed text frames are silently dropped (visual gap < hard error);
 * malformed `complete` / `error` frames surface as `onError` with a
 * `malformed_*` code so contract drift is visible in observability.
 *
 * Reply language (Phase 4c batch 1, 9 Jun 2026): every chat POST
 * carries a `language` field sourced from `i18n.resolvedLanguage` so
 * the server-side persona can respond in the guest's language. The
 * caller never passes language — the hook reads i18next directly
 * via useTranslation. Callers stay simple; the multilingual surface
 * is encapsulated in the transport layer. Defensive: unsupported /
 * unresolved values clip to 'en' before send so the server-side Zod
 * enum never rejects a malformed locale tag.
 *
 * Idempotent retries (Batch 1c, 11 Jun 2026): the input schema
 * accepts an OPTIONAL `clientNonce` (UUID v4). Minted at enqueue
 * time by the outbox (src/lib/outbox.ts) and replayed unchanged on
 * every retry of the same logical send. When present, it forwards
 * straight to the server body via the existing spread; the server's
 * idempotency contract (migration 0021, src/routes/chat.ts) detects
 * the duplicate nonce on retry and replays the cached exchange
 * instead of running the LLM twice. When absent, the request runs
 * the non-idempotent path unchanged — preserves byte-identical
 * behaviour for callers that bypass the outbox (none today, but the
 * field is forward-compatible).
 *
 * Callers DO NOT mint clientNonce themselves — the outbox owns
 * minting (one nonce per enqueued item, immutable across retries).
 * Rotating the nonce on retry defeats the entire contract.
 */
import { useCallback, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { ApiError } from '../api';
import { apiStream } from './sse';
// ─── Supported reply languages ───────────────────────────────────────────
/**
 * Mirrors `i18n/index.ts` supportedLngs and the server-side Zod enum
 * in `towninsider-api/src/routes/chat.ts#SendMessageSchema`. Adding a
 * language = updating both ends in lockstep. Phase 4c batch 1.
 */
const SUPPORTED_LANGUAGES = ['en', 'fr', 'de', 'es'] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return (
    typeof value === 'string' &&
    (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
  );
}
// ─── Input validation ────────────────────────────────────────────────────
/**
 * Caller-facing input shape. `language` is NOT included here — it's
 * sourced internally by the hook from i18next so callers don't have
 * to thread the current language through every chat send. The body
 * actually transmitted to the server adds `language` to this shape
 * (see send() below); the server-side schema in
 * `towninsider-api/src/routes/chat.ts` accepts the extra field with
 * a Zod `.default('en')` so backward compatibility is preserved.
 *
 * `clientNonce` (Batch 1c, optional): UUID v4 minted by the outbox
 * per logical send and replayed on every retry. The hook does NOT
 * mint it — callers (outbox drain loop) supply the nonce from the
 * persisted item. The optional() lets non-outbox callers omit the
 * field entirely; Zod's optional() omits the key from `parsed.data`
 * when absent, so the existing `{ ...parsed.data, language }` body
 * construction below threads it through only when present.
 *
 * Other field limits MUST stay in sync with the server schema —
 * divergence causes pre-stream Zod rejection on the server (4xx JSON
 * envelope surfaced via `onError`). Client-side validation is
 * defence-in-depth; the server is authoritative.
 */
const sendMessageInputSchema = z.object({
  text: z.string().min(1).max(4000),
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  clientNonce: z.string().uuid().optional(),
});
export type SendMessageInput = z.infer<typeof sendMessageInputSchema>;
// ─── SSE event payload schemas ───────────────────────────────────────────
/** `event: text` — stream-time, fires many times per turn. */
const textEventDataSchema = z.object({
  text: z.string(),
});
/**
 * `event: complete` — fires exactly once at the end of a successful
 * exchange, after atomic DB persistence. Both message IDs are
 * server-issued ULIDs. Captured here for forward compatibility; client
 * state still uses local IDs until Batch 1c.
 */
const completeEventDataSchema = z.object({
  userMessageId: z.string().min(1),
  assistantMessageId: z.string().min(1),
  dateLocal: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  tokensIn: z.number().int().nonnegative(),
  tokensOut: z.number().int().nonnegative(),
  toolCallCount: z.number().int().nonnegative(),
});
export type CompleteEventData = z.infer<typeof completeEventDataSchema>;
/**
 * `event: error` — fires once mid-stream when the server cannot
 * complete the exchange. Stream closes cleanly after this event.
 *
 * Known codes from `towninsider-api/src/routes/chat.ts`:
 *   - `persona_overloaded`   (429 / 529 from Anthropic)
 *   - `persona_unreachable`  (other Anthropic failures)
 *   - `tool_loop_exceeded`   (5+ tool iterations without end_turn)
 *   - `empty_response`       (LLM returned no text)
 *   - `persistence_failed`   (stream succeeded, DB write failed)
 *   - `internal`             (catch-all)
 */
const errorEventDataSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
});
export type ErrorEventData = z.infer<typeof errorEventDataSchema>;
// ─── Handlers ────────────────────────────────────────────────────────────
export interface SendMessageHandlers {
  /** Append delta to the assistant placeholder bubble. Fires many times. */
  onTextDelta: (delta: string) => void;
  /** Finalise the exchange. Fires once on success. */
  onComplete: (event: CompleteEventData) => void;
  /**
   * Show persona-voiced soft error. Fires once on pre-stream JSON
   * error OR mid-stream SSE error. Does NOT fire on client-initiated
   * abort — that path is silent.
   */
  onError: (event: ErrorEventData) => void;
}
// ─── Hook ────────────────────────────────────────────────────────────────
export interface UseSendMessageResult {
  /**
   * Open an SSE stream and dispatch events to handlers. Resolves when
   * the stream closes (success / error / abort). Never throws — every
   * failure surface goes through `onError`; aborts are silent.
   */
  send: (
    input: SendMessageInput,
    handlers: SendMessageHandlers,
  ) => Promise<void>;
  /** True between `send()` start and its terminal event. */
  isStreaming: boolean;
  /**
   * Abort the in-flight stream if any. No-op when idle. Intentional
   * aborts do not dispatch `onError`, so it's safe to call from a
   * useEffect cleanup without surfacing UI noise.
   */
  abort: () => void;
}
export function useSendMessage(): UseSendMessageResult {
  const { getToken } = useAuth();
  // Phase 4c batch 1: reply-language sourced from i18next inside the
  // hook so callers don't have to thread it. `resolvedLanguage` gives
  // the active resolved code (preferred over `language` which can be
  // a region-tagged form like 'en-GB' before i18next strips the
  // region per supportedLngs).
  const { i18n } = useTranslation();
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const abort = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);
  const send = useCallback(
    async (
      input: SendMessageInput,
      handlers: SendMessageHandlers,
    ): Promise<void> => {
      // Client-side defence-in-depth. Server is authoritative on
      // validation but pre-empting saves a round-trip on obvious typos.
      const parsed = sendMessageInputSchema.safeParse(input);
      if (!parsed.success) {
        handlers.onError({
          code: 'validation_error',
          message: 'Message could not be sent',
        });
        return;
      }
      // Resolve the reply language from i18next. `resolvedLanguage`
      // is the post-detection, post-fallback active code. Defensive
      // clip to the supported set keeps a stray locale (unresolved
      // detector, manual i18n.changeLanguage to an unsupported code)
      // from hitting the server Zod enum and being rejected.
      const language: SupportedLanguage = isSupportedLanguage(
        i18n.resolvedLanguage,
      )
        ? i18n.resolvedLanguage
        : 'en';
      // Cancel any prior in-flight stream — only one active at a time
      // per hook instance. The previous send()'s for-await loop will
      // throw with `aborted` and return silently via its catch.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      try {
        // Body spreads `parsed.data` which contains text + dateLocal
        // and, when supplied, clientNonce. Zod's `.optional()` on
        // clientNonce omits the key from `parsed.data` when the
        // caller didn't pass it — JSON.stringify therefore drops it
        // from the wire entirely, so non-outbox sends produce a body
        // byte-identical to pre-1c.
        for await (const event of apiStream('/v1/chat/messages', {
          method: 'POST',
          body: { ...parsed.data, language },
          signal: controller.signal,
          getToken,
        })) {
          if (event.event === 'text') {
            const dataParse = textEventDataSchema.safeParse(event.data);
            if (dataParse.success) {
              handlers.onTextDelta(dataParse.data.text);
            }
            // Malformed text frames silently dropped — a visual gap
            // between deltas is less jarring than a hard error mid-flow.
          } else if (event.event === 'complete') {
            const dataParse = completeEventDataSchema.safeParse(event.data);
            if (dataParse.success) {
              handlers.onComplete(dataParse.data);
            } else {
              handlers.onError({
                code: 'malformed_complete',
                message: 'Server response was malformed',
              });
            }
            // Terminal — stop iterating even if the server hasn't
            // closed the stream yet.
            break;
          } else if (event.event === 'error') {
            const dataParse = errorEventDataSchema.safeParse(event.data);
            handlers.onError(
              dataParse.success
                ? dataParse.data
                : {
                    code: 'malformed_error',
                    message: 'An unexpected error occurred',
                  },
            );
            break;
          }
          // Unknown event types ignored — forward-compatible with
          // future SSE additions (e.g. `tool-start` / `tool-end`).
        }
      } catch (err) {
        if (err instanceof ApiError) {
          // Intentional abort — caller cancelled mid-stream. Silent.
          if (err.code === 'aborted') {
            return;
          }
          handlers.onError({
            code: typeof err.code === 'string' ? err.code : 'unknown_error',
            message: err.message,
          });
        } else {
          // Unexpected non-ApiError. Surface generically rather than
          // leak implementation detail.
          handlers.onError({
            code: 'unknown_error',
            message: 'An unexpected error occurred',
          });
        }
      } finally {
        // Only clear state if WE'RE still the active stream — a
        // subsequent send() may have replaced us via abort+create,
        // and we mustn't flip its `isStreaming` back to false.
        if (abortRef.current === controller) {
          abortRef.current = null;
          setIsStreaming(false);
        }
      }
    },
    [getToken, i18n],
  );
  return { send, isStreaming, abort };
}
